import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	deleteValuation,
	duplicateValuation,
	getValuations,
} from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { ValuationWithClassifierRes } from '@dealstack/shared/lib/generated';
import { uuidv7 } from 'uuidv7';
import { ValuationDetailsForm } from '@dealstack/frontend/app/pages/Valuations/constants.types';
import {
	VALUATION_SAVE_MUTATION_KEY,
	VALUATIONS_QUERY_KEY,
} from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';

const emptyList: ValuationWithClassifierRes[] = [];

export function useValuationsQuery(dealId: string | undefined) {
	const queryKey = [VALUATIONS_QUERY_KEY, dealId ?? ''];
	const queryClient = useQueryClient();

	const listQuery = useQuery({
		queryKey,
		queryFn: () => getValuations({ dealId: dealId ?? '' }),
		enabled: !!dealId,
	});

	const deleteValuationMutation = useMutation({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, dealId ?? ''],
		mutationFn: ({ valuationId }: { valuationId: string }) => {
			if (!dealId) {
				throw new Error('Deal ID is required to delete a valuation');
			}
			return deleteValuation({ valuationId, dealId });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey });
		},
	});

	const duplicateValuationMutation = useMutation({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, dealId ?? ''],
		mutationFn: ({ valuationId }: { valuationId: string }) => {
			if (!dealId) {
				throw new Error('Deal ID is required to duplicate a valuation');
			}
			return duplicateValuation({ valuationId, dealId, newValuationId: uuidv7() });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey });
		},
	});

	const updateValuationCache = (updated: ValuationWithClassifierRes) => {
		queryClient.setQueryData<ValuationWithClassifierRes[]>(queryKey, (old) => {
			if (!old) {
				return old;
			}
			return old.map((v) =>
				v.id === updated.id
					? {
							...v,
							...updated,
							totalEquityValue: updated.totalEquityValue ?? v.totalEquityValue,
							totalEnterpriseValue:
								updated.totalEnterpriseValue ?? v.totalEnterpriseValue,
							moic: updated.moic ?? v.moic,
							xirr: updated.xirr ?? v.xirr,
						}
					: v
			);
		});
	};

	const buildUpdatePayload = (formValues: ValuationDetailsForm) => ({
		name: formValues.valuationTitle,
		defaultCurrency: formValues.valuationDefaultCurrency,
		defaultNumberFormat: formValues.valuationMultiples,
		valuationDate: formValues.valuationDate,
		classifierIds: formValues.valuationTags,
	});

	return {
		valuations: listQuery.data ?? emptyList,
		isLoading: listQuery.isLoading,
		refetch: listQuery.refetch,
		deleteValuation: deleteValuationMutation,
		duplicateValuation: duplicateValuationMutation,
		updateValuationCache,
		buildUpdatePayload,
	};
}
